import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { testsData } from "../../Data/testData";
import { useTracking } from "../../hooks/useTracking";

import Instrucciones from "./components/Instrucciones/Instrucciones";
import SeleccionOjo from "./components/SeleccionOjo/SeleccionOjo";
import ImagenPrevia from "./components/ImagenPrevia/ImagenPrevia";
import PreguntasContainer from "./components/Preguntas/PreguntasContainer";
import Decision from "./components/Decision/Decision";
import Resultados from "./components/Resultados/Resultados";

import ImgSeleccionOjo from "../../assets/Page_Test1&Instructions/Page_Test1_Instructions/Icon_Tests1-4.svg";
import imgOk from "../../assets/Page_Results/Icon_Ok.svg";
import imgError from "../../assets/Page_Results/Icon_!.svg";

import "./TestDetalle.css";

const TestDetalle = () => {
  const { testId } = useParams();
  const [searchParams] = useSearchParams();
  const test = testsData[testId];

  const hospitalClient = searchParams.get("hospital") || "Vertex_Lab_Direct";
  const testName = test?.titulo || `test_${testId}`;

  const [paso, setPaso] = useState("instrucciones");
  const [instruccionActual, setInstruccionActual] = useState(0);
  const [ojoSeleccionado, setOjoSeleccionado] = useState(null);
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestasCheckbox, setRespuestasCheckbox] = useState({});
  const [resultadosOjoIzquierdo, setResultadosOjoIzquierdo] = useState([]);
  const [resultadosOjoDerecho, setResultadosOjoDerecho] = useState([]);
  const [ojoCompletado, setOjoCompletado] = useState(null);

  const { trackEvent } = useTracking();

  useEffect(() => {
    if (!test) return;
    trackEvent("test_view", {
      test_name: testName,
      hospital_client: hospitalClient
    });
  }, [test, testName, hospitalClient, trackEvent]);

  useEffect(() => {
    if (paso !== "resultado") return;
    const score =
      resultadosOjoIzquierdo.filter((r) => r.correcto).length +
      resultadosOjoDerecho.filter((r) => r.correcto).length;
    trackEvent("test_complete", {
      test_name: testName,
      hospital_client: hospitalClient,
      score
    });
  }, [
    paso,
    resultadosOjoIzquierdo,
    resultadosOjoDerecho,
    testName,
    hospitalClient,
    trackEvent
  ]);

  if (!test) {
    return <div>Test no encontrado</div>;
  }

  const todasSonCheckbox = test.preguntas.every(
    (p) => p.tipo === "textoCkeckbox"
  );

  // Determinar className dinámico
  const getClassName = () => {
    if (test.id === 4 || test.id === 5) return "color";
    if (test.id === 6) return "miopia";
    return "";
  };

  const siguienteInstruccion = () => {
    if (instruccionActual < test.instrucciones.length - 1) {
      setInstruccionActual(instruccionActual + 1);
    } else {
      if (test.seleccionOjo) {
        setPaso("seleccionOjo");
      } else {
        setPaso("preguntas");
      }
    }
  };

  const seleccionarOjo = (ojo) => {
    setOjoSeleccionado(ojo);
    setPreguntaActual(0);
    setRespuestasCheckbox({});

    if (test.primeraImagen) {
      setPaso("imagenPrevia");
    } else {
      setPaso("preguntas");
    }
  };

  const continuarAPreguntas = () => {
    setPaso("preguntas");
  };

  const responderPregunta = (indiceRespuesta) => {
    const pregunta = test.preguntas[preguntaActual];
    const esCorrecto = indiceRespuesta === pregunta.respuestaCorrecta;
    const resultado = { preguntaId: pregunta.id, correcto: esCorrecto };
    const esUltimaPregunta = preguntaActual === test.preguntas.length - 1;

    if (!test.seleccionOjo) {
      const nuevosResultados = [...resultadosOjoIzquierdo, resultado];
      setResultadosOjoIzquierdo(nuevosResultados);

      if (esUltimaPregunta) {
        setPaso("resultado");
        return;
      } else {
        trackEvent("test_progress", {
          test_name: testName,
          hospital_client: hospitalClient,
          step_number: preguntaActual + 1
        });
        setPreguntaActual(preguntaActual + 1);
        return;
      }
    }

    if (ojoSeleccionado === "izquierdo") {
      const nuevosResultados = [...resultadosOjoIzquierdo, resultado];
      setResultadosOjoIzquierdo(nuevosResultados);

      if (esUltimaPregunta && resultadosOjoDerecho.length > 0) {
        setPaso("resultado");
        return;
      }
    } else {
      const nuevosResultados = [...resultadosOjoDerecho, resultado];
      setResultadosOjoDerecho(nuevosResultados);

      if (esUltimaPregunta && resultadosOjoIzquierdo.length > 0) {
        setPaso("resultado");
        return;
      }
    }

    if (!esUltimaPregunta) {
      trackEvent("test_progress", {
        test_name: testName,
        hospital_client: hospitalClient,
        step_number: preguntaActual + 1
      });
      setPreguntaActual(preguntaActual + 1);
    } else {
      setOjoCompletado(ojoSeleccionado);
      setPaso("decision");
    }
  };

  const responderCheckbox = (preguntaId, indiceRespuesta) => {
    setRespuestasCheckbox({
      ...respuestasCheckbox,
      [preguntaId]: indiceRespuesta
    });
  };

  const finalizarCheckbox = () => {
    const todasRespondidas = test.preguntas.every(
      (pregunta) => respuestasCheckbox[pregunta.id] !== undefined
    );

    if (!todasRespondidas) {
      alert("Por favor responde todas las preguntas");
      return;
    }

    const resultados = test.preguntas.map((pregunta) => ({
      preguntaId: pregunta.id,
      correcto: respuestasCheckbox[pregunta.id] === pregunta.respuestaCorrecta
    }));

    if (!test.seleccionOjo) {
      setResultadosOjoIzquierdo(resultados);
      setPaso("resultado");
      return;
    }

    if (ojoSeleccionado === "izquierdo") {
      setResultadosOjoIzquierdo(resultados);
      if (resultadosOjoDerecho.length > 0) {
        setPaso("resultado");
        return;
      }
    } else {
      setResultadosOjoDerecho(resultados);
      if (resultadosOjoIzquierdo.length > 0) {
        setPaso("resultado");
        return;
      }
    }

    setOjoCompletado(ojoSeleccionado);
    setRespuestasCheckbox({});
    setPaso("decision");
  };

  const continuarConOtroOjo = () => {
    const otroOjo = ojoSeleccionado === "izquierdo" ? "derecho" : "izquierdo";
    setOjoSeleccionado(otroOjo);
    setPreguntaActual(0);
    setRespuestasCheckbox({});

    if (test.primeraImagen) {
      setPaso("imagenPrevia");
    } else {
      setPaso("preguntas");
    }
  };

  const verResultados = () => {
    setPaso("resultado");
  };

  const reiniciarTest = () => {
    setPaso("instrucciones");
    setInstruccionActual(0);
    setOjoSeleccionado(null);
    setPreguntaActual(0);
    setResultadosOjoIzquierdo([]);
    setResultadosOjoDerecho([]);
    setOjoCompletado(null);
    setRespuestasCheckbox({});
  };

  // RENDERIZADO CONDICIONAL
  if (paso === "instrucciones") {
    return (
      <Instrucciones
        instruccion={test.instrucciones[instruccionActual]}
        instruccionActual={instruccionActual}
        totalInstrucciones={test.instrucciones.length}
        onSiguiente={siguienteInstruccion}
        // className={getClassName()}
      />
    );
  }

  if (paso === "seleccionOjo") {
    return (
      <SeleccionOjo
        imgSeleccion={ImgSeleccionOjo}
        onSeleccionar={seleccionarOjo}
        className={getClassName()}
      />
    );
  }

  if (paso === "imagenPrevia") {
    return (
      <ImagenPrevia
        titulo={test.titulo}
        tituloPregunta={test.tituloPreguntaGral}
        imagen={test.imgGral}
        onContinuar={continuarAPreguntas}
        className={getClassName()}
      />
    );
  }

  if (paso === "preguntas") {
    return (
      <PreguntasContainer
        test={test}
        preguntaActual={preguntaActual}
        todasSonCheckbox={todasSonCheckbox}
        respuestasCheckbox={respuestasCheckbox}
        onResponderPregunta={responderPregunta}
        onResponderCheckbox={responderCheckbox}
        onFinalizarCheckbox={finalizarCheckbox}
        className={getClassName()}
      />
    );
  }

  if (paso === "decision") {
    const otroOjo = ojoCompletado === "izquierdo" ? "derecho" : "izquierdo";
    const yaHizoOtroOjo =
      otroOjo === "izquierdo"
        ? resultadosOjoIzquierdo.length > 0
        : resultadosOjoDerecho.length > 0;

    return (
      <Decision
        imgSeleccion={ImgSeleccionOjo}
        ojoCompletado={ojoCompletado}
        yaHizoOtroOjo={yaHizoOtroOjo}
        onContinuar={continuarConOtroOjo}
        onFinalizar={verResultados}
        className={getClassName()}
      />
    );
  }

  if (paso === "resultado") {
    const aciertoIzq = resultadosOjoIzquierdo.filter((r) => r.correcto).length;
    const aciertoDer = resultadosOjoDerecho.filter((r) => r.correcto).length;
    const totalAciertos = aciertoIzq + aciertoDer;
    const totalPreguntas =
      resultadosOjoIzquierdo.length + resultadosOjoDerecho.length;
    const media = totalPreguntas / 2;
    const pasaTest = totalAciertos >= media + 1;

    return (
      <Resultados
        pasaTest={pasaTest}
        imgOk={imgOk}
        imgError={imgError}
        onReiniciar={reiniciarTest}
        className={getClassName()}
        testName={testName}
        hospitalClient={hospitalClient}
      />
    );
  }
};

export default TestDetalle;
